// client/src/contexts/AdvertiserContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { normalizeAdvertiserVertical } from "../config/verticalIntelligenceConfig";

const AdvertiserContext = createContext(null);

// Canonical keys (ID-based)
const KEY_ID = "selectedAdvertiserId";
const KEY_NAME = "selectedAdvertiserName";
const KEY_VERTICAL = "selectedAdvertiserVertical";

// Legacy (some pages used name-only historically)
const LEGACY_NAME = "selectedAdvertiser";

const normalizeVertical = normalizeAdvertiserVertical;

export const AdvertiserProvider = ({ children }) => {
  // ✅ Store ID as STRING (works for numeric IDs, UUIDs, slugs, etc.)
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState(null); // string | null
  const [selectedAdvertiserName, setSelectedAdvertiserName] = useState("");
  const [selectedAdvertiserVertical, setSelectedAdvertiserVertical] = useState("general");
  // False until the mount-time localStorage hydration has run. Eligibility
  // guards must not treat the pre-hydration "general" placeholder as a real
  // vertical, or a film advertiser cold-loading /cinema-analytics would bounce.
  const [advertiserHydrated, setAdvertiserHydrated] = useState(false);

  // ✅ Hydrate once on mount
  useEffect(() => {
    try {
      const storedIdRaw = localStorage.getItem(KEY_ID);
      const storedId =
        storedIdRaw != null && String(storedIdRaw).trim()
          ? String(storedIdRaw).trim()
          : null;

      const storedName =
        localStorage.getItem(KEY_NAME) ||
        localStorage.getItem(LEGACY_NAME) ||
        "";

      if (storedId) setSelectedAdvertiserId(storedId);
      if (storedName) setSelectedAdvertiserName(storedName);
      setSelectedAdvertiserVertical(
        normalizeVertical(localStorage.getItem(KEY_VERTICAL))
      );
    } catch (e) {
      console.warn("[AdvertiserContext] hydrate failed:", e);
    } finally {
      setAdvertiserHydrated(true);
    }
  }, []);

  /**
   * ✅ Canonical setter used by ALL pages
   * Accepts:
   *  - advertiser object: { id, name } (preferred)
   *  - null to clear
   *
   * IMPORTANT:
   * - Allows string IDs (UUID/slug/etc.)
   * - Allows name-only selection as a fallback (ID becomes null)
   */
  // useCallback: state setters and module-level helpers only, so no reactive
  // dependencies — keeps the context useMemo value stable across renders.
  const selectAdvertiser = useCallback((advertiser) => {
    try {
      if (!advertiser) {
        setSelectedAdvertiserId(null);
        setSelectedAdvertiserName("");
        setSelectedAdvertiserVertical("general");
        localStorage.removeItem(KEY_ID);
        localStorage.removeItem(KEY_NAME);
        localStorage.removeItem(KEY_VERTICAL);
        localStorage.removeItem(LEGACY_NAME);
        return;
      }

      const idRaw =
        advertiser.id ??
        advertiser._id ??
        advertiser.advertiserId ??
        advertiser.advertiser_id ??
        advertiser.account_id ??
        advertiser.accountId ??
        advertiser.uuid ??
        advertiser.guid ??
        advertiser.slug ??
        null;

      const name = String(
        advertiser.name ??
          advertiser.advertiser ??
          advertiser.advertiser_name ??
          advertiser.title ??
          advertiser.company ??
          advertiser.businessName ??
          advertiser.displayName ??
          ""
      ).trim();

      const id = idRaw != null && String(idRaw).trim() ? String(idRaw).trim() : null;
      const vertical = normalizeVertical(advertiser.vertical);

      // If we have neither id nor name, do nothing
      if (!id && !name) {
        console.warn("[AdvertiserContext] selectAdvertiser called with empty advertiser:", advertiser);
        return;
      }

      setSelectedAdvertiserId(id);
      setSelectedAdvertiserName(name);
      setSelectedAdvertiserVertical(vertical);

      // Persist
      if (id) localStorage.setItem(KEY_ID, id);
      else localStorage.removeItem(KEY_ID);

      localStorage.setItem(KEY_NAME, name || "");
      localStorage.setItem(KEY_VERTICAL, vertical);
      // legacy for older pages
      localStorage.setItem(LEGACY_NAME, name || "");
    } catch (e) {
      console.warn("[AdvertiserContext] selectAdvertiser failed:", e);
    }
  }, []);

  // Backwards compatibility helpers (if any page still uses these)
  const selectedAdvertiser = selectedAdvertiserName;

  /**
   * ✅ Legacy setter
   * IMPORTANT FIX:
   * - If a page only passes a NAME, we must clear stale ID
   * - Otherwise later pages will incorrectly prefer the old ID
   */
  // useCallback: reads the current name/vertical state to decide whether to
  // preserve the vertical on name-only re-selection, so those are its deps.
  const setSelectedAdvertiser = useCallback((name, advertiser = null) => {
    const safe = String(name || "").trim();
    const storedName =
      localStorage.getItem(KEY_NAME) ||
      localStorage.getItem(LEGACY_NAME) ||
      "";
    const shouldPreserveVertical =
      !advertiser?.vertical &&
      safe &&
      (safe === selectedAdvertiserName || safe === storedName);
    const vertical = normalizeVertical(
      advertiser?.vertical ||
        (shouldPreserveVertical
          ? selectedAdvertiserVertical || localStorage.getItem(KEY_VERTICAL)
          : null)
    );

    // Clear stale ID because this is name-only legacy selection
    setSelectedAdvertiserId(null);
    setSelectedAdvertiserName(safe);
    setSelectedAdvertiserVertical(vertical);

    try {
      localStorage.removeItem(KEY_ID);

      if (safe) {
        localStorage.setItem(KEY_NAME, safe);
        localStorage.setItem(KEY_VERTICAL, vertical);
        localStorage.setItem(LEGACY_NAME, safe);
      } else {
        localStorage.removeItem(KEY_NAME);
        localStorage.removeItem(KEY_VERTICAL);
        localStorage.removeItem(LEGACY_NAME);
      }
    } catch {}
  }, [selectedAdvertiserName, selectedAdvertiserVertical]);

  const value = useMemo(
    () => ({
      // canonical
      selectedAdvertiserId,
      selectedAdvertiserName,
      selectedAdvertiserVertical,
      advertiserHydrated,
      selectAdvertiser,

      // legacy API used by older pages
      selectedAdvertiser,
      setSelectedAdvertiser,
    }),
    [
      selectedAdvertiserId,
      selectedAdvertiserName,
      selectedAdvertiserVertical,
      advertiserHydrated,
      selectedAdvertiser,
      selectAdvertiser,
      setSelectedAdvertiser,
    ]
  );

  return <AdvertiserContext.Provider value={value}>{children}</AdvertiserContext.Provider>;
};

export const useAdvertiser = () => useContext(AdvertiserContext);
